import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import ReactFlow, { Background, Controls, MiniMap } from 'reactflow';
import 'reactflow/dist/style.css';
import {
  Plus, Search, Trash2, Edit, FileText, GitBranch, Sparkles, Brain,
  MessageCircle, ArrowRight, AlertTriangle, Info, Lightbulb, CheckCircle,
  Send, Loader2, Clock, Target, ChevronDown, ChevronUp, Link2, Eye,
  TrendingUp, BarChart3, X, Pin, Paperclip
} from 'lucide-react';
import SensitivitySelect from '@/components/SensitivitySelect';
import SensitivityBadge from '@/components/SensitivityBadge';
import PKMProjectsTab from '@/components/pkm/PKMProjectsTab';
import PKMJournalTab from '@/components/pkm/PKMJournalTab';
import InsightConnectorPanel from '@/components/pkm/InsightConnectorPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NOTE_TYPES = {
  idea: { label: 'Idea', color: '#007AFF', icon: Lightbulb },
  research: { label: 'Research', color: '#34C759', icon: Search },
  meeting: { label: 'Meeting', color: '#FF9500', icon: MessageCircle },
  reflection: { label: 'Reflection', color: '#AF52DE', icon: Brain },
  reference: { label: 'Reference', color: '#4B5563', icon: FileText },
};

const REL_TYPES = {
  supports: { label: 'Supports', color: '#34C759', desc: 'Consistent with' },
  refines: { label: 'Refines', color: '#007AFF', desc: 'Builds upon' },
  contradicts: { label: 'Contradicts', color: '#FF3B30', desc: 'Challenges' },
  supersedes: { label: 'Supersedes', color: '#FF9500', desc: 'Replaces' },
};

const PKMPage = () => {
  const { user, token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get('tab') || 'notes');
  const [notes, setNotes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterProject, setFilterProject] = useState('');
  const [filterType, setFilterType] = useState('');

  // Note dialog
  const [isNoteOpen, setIsNoteOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [noteForm, setNoteForm] = useState({ title: '', content: '', tags: [], project: '', note_type: 'idea', change_summary: '', sensitivity: 'internal' });
  const [tagInput, setTagInput] = useState('');
  const [projectNames, setProjectNames] = useState([]);

  // Note detail
  const [selectedNote, setSelectedNote] = useState(null);
  const [versions, setVersions] = useState([]);  const [relationships, setRelationships] = useState([]);
  const [relatedChats, setRelatedChats] = useState(null);
  const [relatedChatsLoading, setRelatedChatsLoading] = useState(false);
  const [extractingActions, setExtractingActions] = useState(false);

  // Ontology Authoring Assistant
  const [ontAssist, setOntAssist] = useState(null);
  const [ontAssistLoading, setOntAssistLoading] = useState(false);

  // Relationship dialog
  const [isRelOpen, setIsRelOpen] = useState(false);
  const [relForm, setRelForm] = useState({ target_note_id: '', relationship_type: 'supports', description: '' });

  // Evolution map
  const [mapData, setMapData] = useState({ nodes: [], edges: [] });
  const [mapLoading, setMapLoading] = useState(false);

  // Advisor
  const [advisorQuery, setAdvisorQuery] = useState('');
  const [advisorResponse, setAdvisorResponse] = useState(null);
  const [advisorLoading, setAdvisorLoading] = useState(false);
  const [insights, setInsights] = useState([]);
  const [insightsLoading, setInsightsLoading] = useState(false);

  // Decisions
  const [decisions, setDecisions] = useState([]);
  const [isDecisionOpen, setIsDecisionOpen] = useState(false);
  const [decisionForm, setDecisionForm] = useState({ title: '', context: '', options_considered: [], chosen_option: '', reasoning: '', confidence_level: 50, follow_up_days: [7, 30], tags: [], project: '' });
  const [decOptionInput, setDecOptionInput] = useState('');
  const [decTagInput, setDecTagInput] = useState('');
  const [audit, setAudit] = useState(null);
  const [isOutcomeOpen, setIsOutcomeOpen] = useState(false);
  const [selectedDecision, setSelectedDecision] = useState(null);
  const [outcomeForm, setOutcomeForm] = useState({ outcome: 'positive', description: '', lessons_learned: '', score: 50 });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchNotes(); fetchStats(); }, [search, filterProject, filterType]);
  useEffect(() => { fetchProjectNames(); /* eslint-disable-next-line */ }, []);

  const fetchProjectNames = async () => {
    try {
      const res = await axios.get(`${API}/pkm/projects`, { headers });
      setProjectNames((res.data || []).map(p => p.name).filter(Boolean));
    } catch { /* non-fatal */ }
  };

  const fetchNotes = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (filterProject) params.project = filterProject;
      if (filterType) params.note_type = filterType;
      const res = await axios.get(`${API}/pkm/notes`, { params, headers });
      setNotes(res.data);
    } catch { /* */ } finally { setLoading(false); }
  };

  const fetchStats = async () => {
    try { const res = await axios.get(`${API}/pkm/stats`, { headers }); setStats(res.data); } catch { /* */ }
  };

  const fetchVersions = async (noteId) => {
    try { const res = await axios.get(`${API}/pkm/notes/${noteId}/versions`, { headers }); setVersions(res.data); } catch { /* */ }
  };

  const fetchRelationships = async (noteId) => {
    try { const res = await axios.get(`${API}/pkm/notes/${noteId}/relationships`, { headers }); setRelationships(res.data); } catch { /* */ }
  };

  const fetchRelatedChats = async (noteId) => {
    setRelatedChatsLoading(true);
    try {
      const res = await axios.get(`${API}/chat/related-for-note/${noteId}`, { headers });
      setRelatedChats(res.data);
    } catch {
      setRelatedChats({ total: 0, pin_source_count: 0, attachment_count: 0, room_summary: [], messages: [] });
    } finally {
      setRelatedChatsLoading(false);
    }
  };

  const runOntologyAssist = async (noteId) => {
    setOntAssistLoading(true);
    setOntAssist(null);
    try {
      const r = await axios.post(`${API}/ontology/analyze-note`, { note_id: noteId }, { headers });
      setOntAssist(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Ontology assist failed');
    } finally {
      setOntAssistLoading(false);
    }
  };

  const applyClassTag = async (classId) => {
    if (!selectedNote) return;
    const newIds = [...(ontAssist?.existing_class_ids || []), classId];
    try {
      await axios.post(`${API}/ontology/notes/${selectedNote.note_id}/classify`, { class_ids: newIds }, { headers });
      toast.success('Class tag applied');
      setOntAssist({
        ...ontAssist,
        existing_class_ids: newIds,
        suggested_classes: (ontAssist.suggested_classes || []).filter(s => s.class_id !== classId),
      });
    } catch (e) { toast.error(e.response?.data?.detail || 'Tag failed'); }
  };

  const linkIndividual = async (individualId) => {
    if (!selectedNote) return;
    try {
      await axios.post(`${API}/ontology/individuals/link-note`, {
        individual_id: individualId,
        note_id: selectedNote.note_id,
        link: true,
      }, { headers });
      toast.success('Individual linked');
      setOntAssist({
        ...ontAssist,
        mentioned_individuals: (ontAssist.mentioned_individuals || []).filter(m => m.individual_id !== individualId),
      });
    } catch (e) { toast.error(e.response?.data?.detail || 'Link failed'); }
  };

  const createProposedIndividual = async (proposal) => {
    if (!selectedNote) return;
    try {
      await axios.post(`${API}/ontology/individuals`, {
        name: proposal.name,
        class_id: proposal.class_id,
        description: proposal.description,
        linked_note_ids: [selectedNote.note_id],
      }, { headers });
      toast.success(`Created individual "${proposal.name}"`);
      setOntAssist({
        ...ontAssist,
        proposed_individuals: (ontAssist.proposed_individuals || []).filter(p => p.name !== proposal.name),
      });
    } catch (e) { toast.error(e.response?.data?.detail || 'Create failed'); }
  };

  const fetchMap = async () => {
    setMapLoading(true);
    try {
      const params = {};
      if (filterProject) params.project = filterProject;
      const res = await axios.get(`${API}/pkm/evolution-map`, { params, headers });
      setMapData(res.data);
    } catch { /* */ } finally { setMapLoading(false); }
  };

  const fetchDecisions = async () => {
    try { const res = await axios.get(`${API}/pkm/decisions`, { headers }); setDecisions(res.data); } catch { /* */ }
  };

  const fetchAudit = async () => {
    try { const res = await axios.get(`${API}/pkm/decisions/audit/analysis`, { headers }); setAudit(res.data); } catch { /* */ }
  };

  const fetchInsights = async () => {
    setInsightsLoading(true);
    try { const res = await axios.get(`${API}/pkm/advisor/insights`, { headers }); setInsights(res.data.insights || []); } catch { /* */ } finally { setInsightsLoading(false); }
  };

  const handleSaveNote = async () => {
    if (!noteForm.title) { toast.error('Title required'); return; }
    try {
      let savedNoteId;
      if (editingNote) {
        await axios.put(`${API}/pkm/notes/${editingNote.note_id}`, noteForm, { headers });
        toast.success('Note updated (new version created)');
        savedNoteId = editingNote.note_id;
      } else {
        const res = await axios.post(`${API}/pkm/notes`, noteForm, { headers });
        toast.success('Note created');
        savedNoteId = res.data.note_id;
      }
      const contentLen = (noteForm.content || '').length;
      setIsNoteOpen(false); setEditingNote(null);
      setNoteForm({ title: '', content: '', tags: [], project: '', note_type: 'idea', change_summary: '', sensitivity: 'internal' });
      fetchNotes(); fetchStats();

      // Adoption nudge: prompt to extract actions when the note is long enough
      // to plausibly contain action items. Non-blocking, user-initiated.
      if (savedNoteId && contentLen >= 300) {
        toast('Looks like a detailed note — want to pull out action items?', {
          action: {
            label: 'Extract actions',
            onClick: async () => {
              try {
                const r = await axios.post(`${API}/pkm/notes/${savedNoteId}/extract-actions`, {}, { headers });
                const n = r.data.created || 0;
                if (n === 0) toast.info('No actionable items detected.');
                else toast.success(`Created ${n} task${n === 1 ? '' : 's'} in /tasks`);
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Extraction failed');
              }
            },
          },
          duration: 8000,
        });
      }
    } catch { toast.error('Failed'); }
  };

  const handleDeleteNote = async (noteId) => {
    try { await axios.delete(`${API}/pkm/notes/${noteId}`, { headers }); toast.success('Deleted'); fetchNotes(); fetchStats(); setSelectedNote(null); } catch { toast.error('Failed'); }
  };

  const handleCreateRelationship = async () => {
    if (!selectedNote || !relForm.target_note_id) return;
    try {
      await axios.post(`${API}/pkm/notes/${selectedNote.note_id}/relationships`, relForm, { headers });
      toast.success('Relationship created');
      setIsRelOpen(false);
      fetchRelationships(selectedNote.note_id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleAdvisorQuery = async (queryOverride) => {
    const queryText = (typeof queryOverride === 'string' ? queryOverride : advisorQuery).trim();
    if (!queryText) return;
    setAdvisorLoading(true);
    try {
      const res = await axios.post(`${API}/pkm/advisor/query`, { query: queryText, project: filterProject }, { headers });
      setAdvisorResponse(res.data);
    } catch { toast.error('Advisor query failed'); } finally { setAdvisorLoading(false); }
  };

  // Deep-link: PKMPage?tab=advisor&q=<prefilled question> auto-fires the advisor once
  const autoFiredRef = useRef(false);
  const [advisorTaskLoading, setAdvisorTaskLoading] = useState(false);
  const [advisorCreatedTasks, setAdvisorCreatedTasks] = useState(null); // {created, actions}

  const handleCreateTasksFromAdvisor = async () => {
    if (!advisorResponse?.response) return;
    setAdvisorTaskLoading(true);
    setAdvisorCreatedTasks(null);
    try {
      const res = await axios.post(
        `${API}/pkm/advisor/create-tasks`,
        { text: advisorResponse.response, source_query: advisorQuery },
        { headers },
      );
      setAdvisorCreatedTasks(res.data);
      if (res.data.created > 0) {
        toast.success(`Created ${res.data.created} task${res.data.created === 1 ? '' : 's'} in /tasks`);
      } else {
        toast.info('No actionable items detected in this answer');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not extract tasks');
    } finally {
      setAdvisorTaskLoading(false);
    }
  };

  const [advisorDecisionLoading, setAdvisorDecisionLoading] = useState(false);

  const handleLogDecisionFromAdvisor = async () => {
    if (!advisorResponse?.response) return;
    setAdvisorDecisionLoading(true);
    try {
      const res = await axios.post(
        `${API}/pkm/advisor/draft-decision`,
        { text: advisorResponse.response, source_query: advisorQuery },
        { headers },
      );
      const d = res.data.draft || {};
      setDecisionForm({
        title: d.title || '',
        context: d.context || advisorQuery || '',
        options_considered: d.options_considered || [],
        chosen_option: d.chosen_option || '',
        reasoning: d.reasoning || '',
        confidence_level: typeof d.confidence_level === 'number' ? d.confidence_level : 60,
        follow_up_days: d.follow_up_days || [7, 30],
        tags: d.tags || [],
        project: d.project || '',
      });
      setIsDecisionOpen(true);
      toast.success('Decision drafted from advisor answer — review and save');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not draft decision');
    } finally {
      setAdvisorDecisionLoading(false);
    }
  };
  useEffect(() => {
    if (autoFiredRef.current) return;
    const urlTab = searchParams.get('tab');
    const urlQuery = searchParams.get('q');
    if (urlTab === 'advisor' && urlQuery && token) {
      autoFiredRef.current = true;
      setTab('advisor');
      setAdvisorQuery(urlQuery);
      handleAdvisorQuery(urlQuery);
      // Clean the URL so a refresh doesn't re-fire the same question
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateDecision = async () => {
    if (!decisionForm.title) { toast.error('Title required'); return; }
    try {
      await axios.post(`${API}/pkm/decisions`, decisionForm, { headers });
      toast.success('Decision recorded');
      setIsDecisionOpen(false);
      setDecisionForm({ title: '', context: '', options_considered: [], chosen_option: '', reasoning: '', confidence_level: 50, follow_up_days: [7, 30], tags: [], project: '' });
      fetchDecisions();
    } catch { toast.error('Failed'); }
  };

  const handleLogOutcome = async () => {
    if (!selectedDecision) return;
    try {
      await axios.post(`${API}/pkm/decisions/${selectedDecision.decision_id}/outcome`, outcomeForm, { headers });
      toast.success('Outcome logged');
      setIsOutcomeOpen(false);
      fetchDecisions(); fetchAudit();
    } catch { toast.error('Failed'); }
  };

  const openNoteDetail = async (note) => {
    setSelectedNote(note);
    setRelatedChats(null);
    setOntAssist(null);
    fetchVersions(note.note_id);
    fetchRelationships(note.note_id);
    fetchRelatedChats(note.note_id);
  };

  const openEditNote = (note) => {
    setEditingNote(note);
    setNoteForm({ title: note.title, content: note.content, tags: note.tags || [], project: note.project || '', note_type: note.note_type, change_summary: '', sensitivity: note.sensitivity || 'internal' });
    setIsNoteOpen(true);
  };

  const handleExtractActions = async () => {
    if (!selectedNote) return;
    setExtractingActions(true);
    try {
      const res = await axios.post(`${API}/pkm/notes/${selectedNote.note_id}/extract-actions`, {}, { headers });
      const n = res.data.created || 0;
      if (n === 0) {
        toast.info('No actionable items detected in this note.');
      } else {
        toast.success(`Created ${n} task${n === 1 ? '' : 's'} in /tasks from this note`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to extract actions');
    } finally {
      setExtractingActions(false);
    }
  };

  // Load tab data on switch
  useEffect(() => {
    if (tab === 'map') fetchMap();
    if (tab === 'advisor') fetchInsights();
    if (tab === 'decisions') { fetchDecisions(); fetchAudit(); }
  }, [tab]);

  const insightIcons = { warning: AlertTriangle, info: Info, action: Target, suggestion: Lightbulb, insight: Sparkles };
  const insightColors = { warning: '#FF3B30', info: '#007AFF', action: '#FF9500', suggestion: '#34C759', insight: '#AF52DE' };

  return (
    <div data-testid="pkm-page" className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-[#0A0A0A]">Personal Knowledge Engine</h1>
          <p className="text-sm text-[#4B5563]">Temporal notes, AI advisor, and decision intelligence</p>
        </div>
        {stats && (
          <div className="flex gap-3 text-xs">
            <span className="text-[#9CA3AF]">{stats.total_notes} notes</span>
            <span className="text-[#9CA3AF]">{stats.total_relationships} links</span>
            <span className="text-[#9CA3AF]">{stats.total_decisions} decisions</span>
          </div>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="w-full justify-start">
          <TabsTrigger value="notes" data-testid="pkm-tab-notes"><FileText className="w-3.5 h-3.5 mr-1" />Notes</TabsTrigger>
          <TabsTrigger value="projects" data-testid="pkm-tab-projects"><Link2 className="w-3.5 h-3.5 mr-1" />Projects</TabsTrigger>
          <TabsTrigger value="journal" data-testid="pkm-tab-journal"><Pin className="w-3.5 h-3.5 mr-1" />Journal</TabsTrigger>
          <TabsTrigger value="map" data-testid="pkm-tab-map"><GitBranch className="w-3.5 h-3.5 mr-1" />Evolution Map</TabsTrigger>
          <TabsTrigger value="advisor" data-testid="pkm-tab-advisor"><Brain className="w-3.5 h-3.5 mr-1" />Decision Advisor</TabsTrigger>
          <TabsTrigger value="decisions" data-testid="pkm-tab-decisions"><Target className="w-3.5 h-3.5 mr-1" />Decisions Log</TabsTrigger>
        </TabsList>

        {/* ==================== NOTES TAB ==================== */}
        <TabsContent value="notes" className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
              <Input placeholder="Search notes..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="pkm-search" />
            </div>
            <Select value={filterType} onValueChange={v => setFilterType(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[130px]"><SelectValue placeholder="All types" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {Object.entries(NOTE_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {stats?.projects?.length > 0 && (
              <Select value={filterProject} onValueChange={v => setFilterProject(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-[140px]"><SelectValue placeholder="All projects" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All projects</SelectItem>
                  {stats.projects.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={() => { setEditingNote(null); setNoteForm({ title: '', content: '', tags: [], project: '', note_type: 'idea', change_summary: '', sensitivity: 'internal' }); setIsNoteOpen(true); }} data-testid="create-note-btn">
              <Plus className="w-4 h-4 mr-1" />New Note
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Notes List */}
            <div className="lg:col-span-1 space-y-2 max-h-[600px] overflow-y-auto">
              {notes.length === 0 ? (
                <Card className="border-[#E5E7EB]"><CardContent className="p-8 text-center text-[#9CA3AF] text-sm">No notes yet. Create your first note!</CardContent></Card>
              ) : notes.map(n => {
                const cfg = NOTE_TYPES[n.note_type] || NOTE_TYPES.idea;
                const Icon = cfg.icon;
                return (
                  <Card key={n.note_id} className={`border-[#E5E7EB] cursor-pointer transition-all hover:shadow-sm ${selectedNote?.note_id === n.note_id ? 'border-[#002FA7] bg-[#002FA7]/5' : ''}`} onClick={() => openNoteDetail(n)} data-testid={`note-card-${n.note_id}`}>
                    <CardContent className="p-3">
                      <div className="flex items-start gap-2">
                        <div className="w-7 h-7 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: cfg.color + '15' }}>
                          <Icon className="w-3.5 h-3.5" style={{ color: cfg.color }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <h4 className="text-xs font-medium truncate">{n.title}</h4>
                            <Badge variant="secondary" className="text-[8px]">v{n.version}</Badge>
                            {n.sensitivity && n.sensitivity !== 'internal' && <SensitivityBadge level={n.sensitivity} size="sm" />}
                            {n.status === 'superseded' && <Badge className="text-[8px] bg-[#FF9500]/10 text-[#FF9500]">Superseded</Badge>}
                          </div>
                          <p className="text-[10px] text-[#9CA3AF] truncate mt-0.5">{n.content?.substring(0, 80)}</p>
                          {n.tags?.length > 0 && <div className="flex gap-0.5 flex-wrap mt-1">{n.tags.slice(0, 3).map((t, i) => <Badge key={i} variant="secondary" className="text-[8px]">{t}</Badge>)}</div>}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Note Detail */}
            <div className="lg:col-span-2">
              {!selectedNote ? (
                <Card className="border-[#E5E7EB] h-full"><CardContent className="p-12 text-center text-[#9CA3AF]"><FileText className="w-10 h-10 mx-auto mb-3 text-[#E5E7EB]" /><p className="text-sm">Select a note to view details</p></CardContent></Card>
              ) : (
                <Card className="border-[#E5E7EB]" data-testid="note-detail">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-base">{selectedNote.title}</CardTitle>
                        <div className="flex items-center gap-2 mt-1 text-xs text-[#9CA3AF]">
                          <Badge className={`text-[9px]`} style={{ backgroundColor: (NOTE_TYPES[selectedNote.note_type]?.color || '#4B5563') + '15', color: NOTE_TYPES[selectedNote.note_type]?.color || '#4B5563' }}>{NOTE_TYPES[selectedNote.note_type]?.label}</Badge>
                          <span>v{selectedNote.version}</span>
                          {selectedNote.project && <span>{selectedNote.project}</span>}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openEditNote(selectedNote)} data-testid="edit-note-btn"><Edit className="w-3 h-3 mr-1" />Edit</Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setIsRelOpen(true)} data-testid="add-relationship-btn"><Link2 className="w-3 h-3 mr-1" />Link</Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs border-[#FF9500] text-[#FF9500] hover:bg-[#FF9500]/5" onClick={handleExtractActions} disabled={extractingActions} data-testid="extract-actions-btn">
                          {extractingActions ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle className="w-3 h-3 mr-1" />}
                          Extract Actions
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 text-xs text-[#FF3B30]" onClick={() => handleDeleteNote(selectedNote.note_id)}><Trash2 className="w-3 h-3" /></Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="text-sm text-[#4B5563] whitespace-pre-wrap leading-relaxed bg-[#F9FAFB] p-3 rounded-lg" data-testid="note-content">{selectedNote.content}</div>
                    {selectedNote.tags?.length > 0 && <div className="flex gap-1 flex-wrap">{selectedNote.tags.map((t, i) => <Badge key={i} variant="secondary" className="text-[10px]">{t}</Badge>)}</div>}

                    {/* Relationships */}
                    <div>
                      <h4 className="text-xs font-semibold mb-2 flex items-center gap-1"><GitBranch className="w-3.5 h-3.5 text-[#002FA7]" />Relationships ({relationships.length})</h4>
                      {relationships.length === 0 ? <p className="text-[10px] text-[#9CA3AF]">No relationships yet</p> : (
                        <div className="space-y-1">
                          {relationships.map(r => {
                            const rcfg = REL_TYPES[r.relationship_type] || REL_TYPES.supports;
                            const isSource = r.source_note_id === selectedNote.note_id;
                            return (
                              <div key={r.relationship_id} className="flex items-center gap-2 p-2 rounded border border-[#E5E7EB] text-xs" data-testid={`rel-${r.relationship_id}`}>
                                <Badge className="text-[8px]" style={{ backgroundColor: rcfg.color + '15', color: rcfg.color }}>{rcfg.label}</Badge>
                                <span className="text-[#4B5563]">{isSource ? r.target_title : r.source_title}</span>
                                {r.auto_detected && <Badge variant="secondary" className="text-[7px]">AI</Badge>}
                                <span className="text-[#9CA3AF] ml-auto text-[10px]">{Math.round((r.confidence || 1) * 100)}%</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Version History */}
                    <div>
                      <h4 className="text-xs font-semibold mb-2 flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-[#FF9500]" />Version History ({versions.length})</h4>
                      <div className="space-y-1 max-h-[150px] overflow-y-auto">
                        {versions.map(v => (
                          <div key={v.version_id} className="flex items-center gap-2 p-1.5 rounded text-[10px] border border-[#E5E7EB]">
                            <Badge variant="secondary" className="text-[8px]">v{v.version}</Badge>
                            <span className="text-[#4B5563]">{v.change_summary}</span>
                            <span className="text-[#9CA3AF] ml-auto">{new Date(v.created_at).toLocaleDateString()}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Related Chat Discussions */}
                    <div data-testid="related-chats-panel">
                      <h4 className="text-xs font-semibold mb-2 flex items-center gap-1">
                        <MessageCircle className="w-3.5 h-3.5 text-[#34C759]" />
                        Related Chat Discussions{relatedChats ? ` (${relatedChats.total})` : ''}
                      </h4>
                      {relatedChatsLoading ? (
                        <div className="flex items-center gap-2 text-[10px] text-[#9CA3AF]" data-testid="related-chats-loading">
                          <Loader2 className="w-3 h-3 animate-spin" /> Loading chat context…
                        </div>
                      ) : !relatedChats || relatedChats.total === 0 ? (
                        <p className="text-[10px] text-[#9CA3AF]" data-testid="related-chats-empty">
                          This note hasn't been pinned from or attached in any chat yet.
                        </p>
                      ) : (
                        <div className="space-y-2" data-testid="related-chats-content">
                          {/* Summary chips */}
                          <div className="flex flex-wrap gap-1.5">
                            {relatedChats.pin_source_count > 0 && (
                              <Badge className="text-[9px] bg-[#007AFF]/10 text-[#007AFF] hover:bg-[#007AFF]/10" data-testid="related-chats-pin-count">
                                <Pin className="w-2.5 h-2.5 mr-1" />{relatedChats.pin_source_count} pinned from
                              </Badge>
                            )}
                            {relatedChats.attachment_count > 0 && (
                              <Badge className="text-[9px] bg-[#34C759]/10 text-[#34C759] hover:bg-[#34C759]/10" data-testid="related-chats-attach-count">
                                <Paperclip className="w-2.5 h-2.5 mr-1" />{relatedChats.attachment_count} attached in
                              </Badge>
                            )}
                            {(relatedChats.room_summary || []).slice(0, 4).map(r => (
                              <Badge key={r.room_label} variant="secondary" className="text-[9px]" data-testid={`related-chats-room-${r.room_label}`}>
                                #{r.room_label} · {r.count}
                              </Badge>
                            ))}
                          </div>

                          {/* Message list */}
                          <div className="space-y-1 max-h-[200px] overflow-y-auto">
                            {(relatedChats.messages || []).slice(0, 10).map(m => (
                              <a
                                key={m.message_id}
                                href={`/workspace?tab=chat&room=${m.room_id}&message=${m.message_id}`}
                                className="block p-2 rounded border border-[#E5E7EB] text-[10px] hover:bg-[#F9FAFB] transition-colors"
                                data-testid={`related-chat-${m.message_id}`}
                              >
                                <div className="flex items-center gap-1.5 mb-0.5">
                                  <span className="font-medium text-[#111827]">{m.user_name || 'Unknown'}</span>
                                  <span className="text-[#9CA3AF]">in</span>
                                  <Badge variant="secondary" className="text-[8px] py-0">#{m.room_label}</Badge>
                                  {m.is_pin_source && (
                                    <Badge className="text-[8px] py-0 bg-[#007AFF]/10 text-[#007AFF] hover:bg-[#007AFF]/10">
                                      <Pin className="w-2 h-2 mr-0.5" />pin source
                                    </Badge>
                                  )}
                                  {m.is_attachment && (
                                    <Badge className="text-[8px] py-0 bg-[#34C759]/10 text-[#34C759] hover:bg-[#34C759]/10">
                                      <Paperclip className="w-2 h-2 mr-0.5" />attached
                                    </Badge>
                                  )}
                                  <span className="text-[#9CA3AF] ml-auto">{m.created_at ? new Date(m.created_at).toLocaleDateString() : ''}</span>
                                </div>
                                <div className="text-[#4B5563] line-clamp-2">{m.content || <em className="text-[#9CA3AF]">(note attachment only)</em>}</div>
                              </a>
                            ))}
                            {relatedChats.messages && relatedChats.messages.length > 10 && (
                              <p className="text-[9px] text-[#9CA3AF] text-center pt-1">
                                Showing 10 of {relatedChats.messages.length} references
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Ontology Authoring Assistant */}
                    <div data-testid="ontology-assist-panel" className="pt-3 border-t border-[#F3F4F6]">
                      <div className="flex items-center justify-between mb-1.5">
                        <h4 className="text-xs font-semibold flex items-center gap-1">
                          <Sparkles className="w-3.5 h-3.5 text-[#AF52DE]" />
                          Ontology Assist
                        </h4>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 text-[10px]"
                          onClick={() => runOntologyAssist(selectedNote.note_id)}
                          disabled={ontAssistLoading}
                          data-testid="ontology-assist-run"
                        >
                          {ontAssistLoading ? <><Loader2 className="w-3 h-3 animate-spin mr-1" />Analysing…</> : 'Analyze with AI'}
                        </Button>
                      </div>
                      {!ontAssist && !ontAssistLoading && (
                        <p className="text-[10px] text-[#9CA3AF]">Get GPT-5.2 suggestions for ontology class tags, mentioned individuals, and new individuals to extract.</p>
                      )}
                      {ontAssist && (
                        <div className="space-y-2">
                          {(ontAssist.suggested_classes || []).length > 0 && (
                            <div data-testid="assist-suggested-classes">
                              <p className="text-[10px] font-medium text-[#4B5563] mb-1">Suggested class tags</p>
                              <div className="flex flex-wrap gap-1">
                                {ontAssist.suggested_classes.map(s => (
                                  <button
                                    key={s.class_id}
                                    type="button"
                                    className="text-[10px] px-2 py-0.5 rounded-full bg-[#002FA7]/10 text-[#002FA7] hover:bg-[#002FA7]/20 flex items-center gap-1"
                                    title={s.rationale}
                                    onClick={() => applyClassTag(s.class_id)}
                                    data-testid={`assist-apply-class-${s.class_id}`}
                                  >
                                    <Plus className="w-2.5 h-2.5" />
                                    {s.name}
                                    <span className="text-[8px] opacity-70">{Math.round(s.confidence * 100)}%</span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                          {(ontAssist.mentioned_individuals || []).length > 0 && (
                            <div data-testid="assist-mentioned-individuals">
                              <p className="text-[10px] font-medium text-[#4B5563] mb-1">Mentions existing individuals</p>
                              <div className="space-y-1">
                                {ontAssist.mentioned_individuals.map(m => (
                                  <div key={m.individual_id} className="flex flex-wrap items-start gap-1.5 p-1.5 rounded border border-[#E5E7EB] text-[10px]">
                                    <span className="font-medium text-[#111827] flex-1">{m.name}</span>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-5 text-[9px] px-1.5"
                                      onClick={() => linkIndividual(m.individual_id)}
                                      data-testid={`assist-link-${m.individual_id}`}
                                    >
                                      <Link2 className="w-2.5 h-2.5 mr-0.5" />Link
                                    </Button>
                                    {m.evidence && <span className="w-full text-[9px] text-[#9CA3AF] italic mt-1">&ldquo;{m.evidence}&rdquo;</span>}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {(ontAssist.proposed_individuals || []).length > 0 && (
                            <div data-testid="assist-proposed-individuals">
                              <p className="text-[10px] font-medium text-[#4B5563] mb-1">Proposed new individuals</p>
                              <div className="space-y-1">
                                {ontAssist.proposed_individuals.map((p, i) => (
                                  <div key={i} className="p-1.5 rounded border border-[#E5E7EB] text-[10px]">
                                    <div className="flex items-center gap-1.5">
                                      <span className="font-medium text-[#111827] flex-1">{p.name}</span>
                                      <Badge variant="secondary" className="text-[8px]">{p.class_name}</Badge>
                                      <Button
                                        size="sm"
                                        className="h-5 text-[9px] px-1.5 bg-[#34C759] hover:bg-[#28a745]"
                                        onClick={() => createProposedIndividual(p)}
                                        data-testid={`assist-create-${i}`}
                                      >
                                        <Plus className="w-2.5 h-2.5 mr-0.5" />Create
                                      </Button>
                                    </div>
                                    {p.description && <p className="text-[9px] text-[#4B5563] mt-0.5">{p.description}</p>}
                                    {p.evidence && <p className="text-[9px] text-[#9CA3AF] italic mt-0.5">&ldquo;{p.evidence}&rdquo;</p>}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {(ontAssist.suggested_classes || []).length === 0 &&
                           (ontAssist.mentioned_individuals || []).length === 0 &&
                           (ontAssist.proposed_individuals || []).length === 0 && (
                            <p className="text-[10px] text-[#9CA3AF]">No confident suggestions for this note.</p>
                          )}
                        </div>
                      )}
                    </div>

                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>

        {/* ==================== EVOLUTION MAP TAB ==================== */}
        <TabsContent value="projects">
          <PKMProjectsTab token={token} />
        </TabsContent>

        <TabsContent value="journal">
          <PKMJournalTab token={token} />
        </TabsContent>

        <TabsContent value="map" className="space-y-4">
          <InsightConnectorPanel token={token} onAccepted={() => { fetchMap(); if (selectedNote) fetchRelationships(selectedNote.note_id); }} />
          <Card className="border-[#E5E7EB]" data-testid="evolution-map">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2"><GitBranch className="w-4 h-4 text-[#002FA7]" />Evolution Map ({mapData.total_notes} notes, {mapData.total_relationships} links)</CardTitle>
                <div className="flex gap-2">
                  {Object.entries(REL_TYPES).map(([k, v]) => (
                    <div key={k} className="flex items-center gap-1 text-[10px]">
                      <div className="w-3 h-0.5 rounded" style={{ backgroundColor: v.color }} />
                      <span className="text-[#9CA3AF]">{v.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div style={{ height: 500 }}>
                {mapLoading ? (
                  <div className="flex items-center justify-center h-full"><Loader2 className="w-6 h-6 animate-spin text-[#002FA7]" /></div>
                ) : mapData.nodes.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-[#9CA3AF] text-sm">No notes to display. Create notes to build your evolution map.</div>
                ) : (
                  <ReactFlow nodes={mapData.nodes} edges={mapData.edges} fitView>
                    <Background color="#E5E7EB" gap={20} />
                    <Controls />
                    <MiniMap style={{ height: 80 }} />
                  </ReactFlow>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ==================== ADVISOR TAB ==================== */}
        <TabsContent value="advisor" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Query Panel */}
            <div className="lg:col-span-2">
              <Card className="border-[#E5E7EB]" data-testid="advisor-panel">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2"><Brain className="w-4 h-4 text-[#002FA7]" />Decision Advisor</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-xs text-[#4B5563]">Ask questions about your knowledge base. The AI will synthesize insights from your notes, relationships, and past decisions.</p>
                  <div className="flex gap-2">
                    <Input value={advisorQuery} onChange={e => setAdvisorQuery(e.target.value)} placeholder="e.g., What common pitfalls should I watch for in this project?" className="flex-1" onKeyDown={e => e.key === 'Enter' && handleAdvisorQuery()} data-testid="advisor-query-input" />
                    <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={handleAdvisorQuery} disabled={advisorLoading} data-testid="advisor-send-btn">
                      {advisorLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {['What are the key risks in my current project?', 'Summarize contradicting viewpoints in my notes', 'What knowledge gaps should I address?'].map((q, i) => (
                      <button key={i} onClick={() => { setAdvisorQuery(q); }} className="text-[10px] px-2 py-1 rounded-full bg-[#F3F4F6] text-[#4B5563] hover:bg-[#E5E7EB] transition-colors">{q}</button>
                    ))}
                  </div>
                  {advisorResponse && (
                    <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg p-4" data-testid="advisor-response">
                      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                        <div className="flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-[#002FA7]" />
                          <span className="text-xs font-semibold">AI Synthesis</span>
                          <span className="text-[10px] text-[#9CA3AF]">({advisorResponse.context?.notes_used} notes, {advisorResponse.context?.relationships_used} links)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleLogDecisionFromAdvisor}
                            disabled={advisorDecisionLoading}
                            className="h-7 text-xs"
                            data-testid="advisor-log-decision-btn"
                          >
                            {advisorDecisionLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <GitBranch className="w-3 h-3 mr-1" />}
                            Log as decision
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleCreateTasksFromAdvisor}
                            disabled={advisorTaskLoading}
                            className="h-7 text-xs"
                            data-testid="advisor-create-tasks-btn"
                          >
                            {advisorTaskLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Target className="w-3 h-3 mr-1" />}
                            Create tasks from this answer
                          </Button>
                        </div>
                      </div>
                      <div className="text-xs text-[#4B5563] leading-relaxed whitespace-pre-wrap max-h-[300px] overflow-y-auto">{advisorResponse.response}</div>
                      {advisorCreatedTasks && advisorCreatedTasks.created > 0 && (
                        <div className="mt-3 pt-3 border-t border-[#E5E7EB]" data-testid="advisor-tasks-created">
                          <div className="flex items-center gap-2 mb-1.5">
                            <CheckCircle className="w-3.5 h-3.5 text-[#34C759]" />
                            <span className="text-xs font-semibold text-[#0A0A0A]">
                              {advisorCreatedTasks.created} task{advisorCreatedTasks.created === 1 ? '' : 's'} created
                            </span>
                            <a href="/tasks" className="text-[11px] text-[#002FA7] hover:underline ml-auto">View in /tasks →</a>
                          </div>
                          <ul className="space-y-1">
                            {(advisorCreatedTasks.actions || []).map((a, i) => (
                              <li key={i} className="text-[11px] text-[#4B5563] flex items-start gap-2">
                                <span className={`mt-0.5 px-1 py-0.5 rounded text-[9px] font-semibold text-white ${a.priority === 'high' ? 'bg-[#FF3B30]' : a.priority === 'low' ? 'bg-[#9CA3AF]' : 'bg-[#FF9500]'}`}>
                                  {(a.priority || 'medium').toUpperCase()}
                                </span>
                                <span>{a.title}{a.assigned_to_name ? ` → ${a.assigned_to_name}` : ''}{a.due_hint ? ` · ${a.due_hint}` : ''}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Proactive Insights */}
            <div>
              <Card className="border-[#E5E7EB]" data-testid="insights-panel">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm flex items-center gap-2"><Lightbulb className="w-4 h-4 text-[#FF9500]" />Proactive Insights</CardTitle>
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={fetchInsights} disabled={insightsLoading}>
                      {insightsLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  {insights.length === 0 ? (
                    <p className="text-[10px] text-[#9CA3AF] text-center py-4">Add more notes to generate proactive insights</p>
                  ) : insights.map((ins, i) => {
                    const Icon = insightIcons[ins.type] || Lightbulb;
                    const color = insightColors[ins.type] || '#9CA3AF';
                    return (
                      <div key={i} className="p-2.5 rounded-lg border border-[#E5E7EB]" data-testid={`insight-${i}`}>
                        <div className="flex items-center gap-1.5 mb-1">
                          <Icon className="w-3.5 h-3.5" style={{ color }} />
                          <span className="text-xs font-medium">{ins.title}</span>
                        </div>
                        <p className="text-[10px] text-[#4B5563] leading-relaxed">{ins.description}</p>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* ==================== DECISIONS TAB ==================== */}
        <TabsContent value="decisions" className="space-y-4">
          <div className="flex items-center justify-between">
            <div />
            <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={() => setIsDecisionOpen(true)} data-testid="create-decision-btn"><Plus className="w-4 h-4 mr-1" />Record Decision</Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Decisions List */}
            <div className="lg:col-span-2 space-y-2">
              {decisions.length === 0 ? (
                <Card className="border-[#E5E7EB]"><CardContent className="p-8 text-center text-[#9CA3AF] text-sm">No decisions recorded yet</CardContent></Card>
              ) : decisions.map(d => (
                <Card key={d.decision_id} className="border-[#E5E7EB]" data-testid={`decision-${d.decision_id}`}>
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="text-sm font-medium">{d.title}</h4>
                          <Badge className={d.outcome_status === 'logged' ? 'text-[9px] bg-[#34C759]/10 text-[#34C759]' : 'text-[9px] bg-[#FF9500]/10 text-[#FF9500]'}>{d.outcome_status === 'logged' ? 'Outcome Logged' : 'Pending'}</Badge>
                          <Badge variant="secondary" className="text-[8px]">{d.confidence_level}% confidence</Badge>
                        </div>
                        <p className="text-xs text-[#4B5563] mt-1">{d.context?.substring(0, 150)}</p>
                        <div className="flex items-center gap-2 mt-1.5 text-[10px] text-[#9CA3AF]">
                          <span>Chose: {d.chosen_option}</span>
                          {d.linked_notes?.length > 0 && <span>{d.linked_notes.length} linked notes</span>}
                        </div>
                        {d.outcomes?.length > 0 && d.outcomes.map((o, i) => (
                          <div key={i} className="mt-2 p-2 rounded bg-[#F9FAFB] text-xs">
                            <Badge className={`text-[8px] ${o.outcome === 'positive' ? 'bg-[#34C759]/10 text-[#34C759]' : o.outcome === 'negative' ? 'bg-[#FF3B30]/10 text-[#FF3B30]' : 'bg-[#9CA3AF]/10 text-[#4B5563]'}`}>{o.outcome} ({o.score}/100)</Badge>
                            <p className="text-[#4B5563] mt-1">{o.description}</p>
                            {o.lessons_learned && <p className="text-[#9CA3AF] mt-0.5 italic">{o.lessons_learned}</p>}
                          </div>
                        ))}
                      </div>
                      {d.outcome_status === 'pending' && (
                        <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => { setSelectedDecision(d); setOutcomeForm({ outcome: 'positive', description: '', lessons_learned: '', score: 50 }); setIsOutcomeOpen(true); }} data-testid={`log-outcome-${d.decision_id}`}>
                          <CheckCircle className="w-3 h-3 mr-1" />Log Outcome
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Decision Audit */}
            <div>
              <Card className="border-[#E5E7EB]" data-testid="decision-audit">
                <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><BarChart3 className="w-4 h-4 text-[#AF52DE]" />Decision Audit</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  {!audit || audit.total === 0 ? (
                    <p className="text-[10px] text-[#9CA3AF] text-center py-4">Record decisions to see audit analytics</p>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="p-2 rounded bg-[#F9FAFB] text-center"><p className="text-lg font-bold text-[#002FA7]">{audit.total}</p><p className="text-[10px] text-[#9CA3AF]">Total</p></div>
                        <div className="p-2 rounded bg-[#F9FAFB] text-center"><p className="text-lg font-bold text-[#34C759]">{audit.follow_up_rate}%</p><p className="text-[10px] text-[#9CA3AF]">Follow-up Rate</p></div>
                        <div className="p-2 rounded bg-[#F9FAFB] text-center"><p className="text-lg font-bold text-[#FF9500]">{audit.avg_score}</p><p className="text-[10px] text-[#9CA3AF]">Avg Score</p></div>
                        <div className="p-2 rounded bg-[#F9FAFB] text-center"><p className="text-lg font-bold text-[#AF52DE]">{audit.pending}</p><p className="text-[10px] text-[#9CA3AF]">Pending</p></div>
                      </div>
                      {(audit.high_confidence_avg_score > 0 || audit.low_confidence_avg_score > 0) && (
                        <div className="p-2 rounded border border-[#E5E7EB] text-xs">
                          <p className="font-medium mb-1">Confidence vs Outcome</p>
                          <p className="text-[#4B5563]">High confidence: avg score {audit.high_confidence_avg_score}</p>
                          <p className="text-[#4B5563]">Low confidence: avg score {audit.low_confidence_avg_score}</p>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* ==================== DIALOGS ==================== */}

      {/* Note Dialog */}
      <Dialog open={isNoteOpen} onOpenChange={setIsNoteOpen}>
        <DialogContent data-testid="note-dialog">
          <DialogHeader><DialogTitle>{editingNote ? 'Edit Note' : 'New Note'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title *</Label><Input value={noteForm.title} onChange={e => setNoteForm({...noteForm, title: e.target.value})} data-testid="note-title-input" /></div>
            <div><Label>Content</Label><Textarea value={noteForm.content} onChange={e => setNoteForm({...noteForm, content: e.target.value})} rows={5} data-testid="note-content-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Select value={noteForm.note_type} onValueChange={v => setNoteForm({...noteForm, note_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(NOTE_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Project</Label>
                <Input
                  value={noteForm.project}
                  onChange={e => setNoteForm({...noteForm, project: e.target.value})}
                  placeholder="Project name"
                  list="pkm-project-suggestions"
                  data-testid="note-project-input"
                />
                <datalist id="pkm-project-suggestions">
                  {projectNames.map(n => <option key={n} value={n} />)}
                </datalist>
                {projectNames.length > 0 && (
                  <p className="text-[10px] text-[#9CA3AF] mt-0.5">
                    Tip: pick from your existing projects for Deep Dive + streak to track this note.
                  </p>
                )}
              </div>
            </div>
            <div>
              <Label>Tags</Label>
              <div className="flex gap-2">
                <Input value={tagInput} onChange={e => setTagInput(e.target.value)} placeholder="Add tag..." onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (tagInput.trim()) { setNoteForm({...noteForm, tags: [...noteForm.tags, tagInput.trim()]}); setTagInput(''); }}}} />
                <Button variant="outline" size="sm" onClick={() => { if (tagInput.trim()) { setNoteForm({...noteForm, tags: [...noteForm.tags, tagInput.trim()]}); setTagInput(''); }}}>Add</Button>
              </div>
              {noteForm.tags.length > 0 && <div className="flex gap-1 flex-wrap mt-1">{noteForm.tags.map((t, i) => <Badge key={i} variant="secondary" className="text-[10px] cursor-pointer" onClick={() => setNoteForm({...noteForm, tags: noteForm.tags.filter((_, j) => j !== i)})}>{t} x</Badge>)}</div>}
            </div>
            {editingNote && <div><Label>Change Summary</Label><Input value={noteForm.change_summary} onChange={e => setNoteForm({...noteForm, change_summary: e.target.value})} placeholder="What changed?" /></div>}
            <SensitivitySelect value={noteForm.sensitivity} onChange={v => setNoteForm({...noteForm, sensitivity: v})} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsNoteOpen(false)}>Cancel</Button>
            <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={handleSaveNote} data-testid="save-note-btn">{editingNote ? 'Save Version' : 'Create Note'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Relationship Dialog */}
      <Dialog open={isRelOpen} onOpenChange={setIsRelOpen}>
        <DialogContent data-testid="relationship-dialog">
          <DialogHeader><DialogTitle>Create Relationship</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Target Note</Label>
              <Select value={relForm.target_note_id} onValueChange={v => setRelForm({...relForm, target_note_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select note" /></SelectTrigger>
                <SelectContent>{notes.filter(n => n.note_id !== selectedNote?.note_id).map(n => <SelectItem key={n.note_id} value={n.note_id}>{n.title}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Relationship Type</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {Object.entries(REL_TYPES).map(([k, v]) => (
                  <button key={k} onClick={() => setRelForm({...relForm, relationship_type: k})} className={`p-2 rounded border text-left transition-all ${relForm.relationship_type === k ? 'border-[#002FA7] bg-[#002FA7]/5' : 'border-[#E5E7EB]'}`}>
                    <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full" style={{ backgroundColor: v.color }} /><span className="text-xs font-medium">{v.label}</span></div>
                    <p className="text-[10px] text-[#9CA3AF] mt-0.5">{v.desc}</p>
                  </button>
                ))}
              </div>
            </div>
            <div><Label>Description</Label><Input value={relForm.description} onChange={e => setRelForm({...relForm, description: e.target.value})} placeholder="Why are these related?" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRelOpen(false)}>Cancel</Button>
            <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={handleCreateRelationship} data-testid="save-relationship-btn">Create Link</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Decision Dialog */}
      <Dialog open={isDecisionOpen} onOpenChange={setIsDecisionOpen}>
        <DialogContent className="max-w-xl" data-testid="decision-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Target className="w-5 h-5 text-[#002FA7]" />Record Decision</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            <div><Label>Decision Title *</Label><Input value={decisionForm.title} onChange={e => setDecisionForm({...decisionForm, title: e.target.value})} data-testid="decision-title-input" /></div>
            <div><Label>Context</Label><Textarea value={decisionForm.context} onChange={e => setDecisionForm({...decisionForm, context: e.target.value})} rows={3} placeholder="What situation led to this decision?" /></div>
            <div>
              <Label>Options Considered</Label>
              <div className="flex gap-2"><Input value={decOptionInput} onChange={e => setDecOptionInput(e.target.value)} placeholder="Add option..." onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (decOptionInput.trim()) { setDecisionForm({...decisionForm, options_considered: [...decisionForm.options_considered, decOptionInput.trim()]}); setDecOptionInput(''); }}}}/><Button variant="outline" size="sm" onClick={() => { if (decOptionInput.trim()) { setDecisionForm({...decisionForm, options_considered: [...decisionForm.options_considered, decOptionInput.trim()]}); setDecOptionInput(''); }}}>Add</Button></div>
              {decisionForm.options_considered.length > 0 && <div className="flex gap-1 flex-wrap mt-1">{decisionForm.options_considered.map((o, i) => <Badge key={i} variant="secondary" className="text-[10px] cursor-pointer" onClick={() => setDecisionForm({...decisionForm, options_considered: decisionForm.options_considered.filter((_, j) => j !== i)})}>{o} x</Badge>)}</div>}
            </div>
            <div><Label>Chosen Option *</Label><Input value={decisionForm.chosen_option} onChange={e => setDecisionForm({...decisionForm, chosen_option: e.target.value})} /></div>
            <div><Label>Reasoning</Label><Textarea value={decisionForm.reasoning} onChange={e => setDecisionForm({...decisionForm, reasoning: e.target.value})} rows={2} placeholder="Why this option?" /></div>
            <div>
              <Label>Confidence Level: {decisionForm.confidence_level}%</Label>
              <input type="range" min={0} max={100} value={decisionForm.confidence_level} onChange={e => setDecisionForm({...decisionForm, confidence_level: parseInt(e.target.value)})} className="w-full" />
            </div>
            <div>
              <Label>Project</Label>
              <Input
                value={decisionForm.project}
                onChange={e => setDecisionForm({...decisionForm, project: e.target.value})}
                list="pkm-project-suggestions"
                placeholder="Project name"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDecisionOpen(false)}>Cancel</Button>
            <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={handleCreateDecision} data-testid="save-decision-btn">Record Decision</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Outcome Dialog */}
      <Dialog open={isOutcomeOpen} onOpenChange={setIsOutcomeOpen}>
        <DialogContent data-testid="outcome-dialog">
          <DialogHeader><DialogTitle>Log Decision Outcome</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Outcome</Label>
              <div className="grid grid-cols-4 gap-2 mt-1">
                {['positive', 'negative', 'neutral', 'mixed'].map(o => (
                  <button key={o} onClick={() => setOutcomeForm({...outcomeForm, outcome: o})} className={`p-2 rounded border text-xs font-medium text-center capitalize transition-all ${outcomeForm.outcome === o ? 'border-[#002FA7] bg-[#002FA7]/5 text-[#002FA7]' : 'border-[#E5E7EB]'}`}>{o}</button>
                ))}
              </div>
            </div>
            <div><Label>Description *</Label><Textarea value={outcomeForm.description} onChange={e => setOutcomeForm({...outcomeForm, description: e.target.value})} rows={3} placeholder="What happened?" /></div>
            <div><Label>Lessons Learned</Label><Textarea value={outcomeForm.lessons_learned} onChange={e => setOutcomeForm({...outcomeForm, lessons_learned: e.target.value})} rows={2} placeholder="What did you learn?" /></div>
            <div><Label>Score: {outcomeForm.score}/100</Label><input type="range" min={0} max={100} value={outcomeForm.score} onChange={e => setOutcomeForm({...outcomeForm, score: parseInt(e.target.value)})} className="w-full" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsOutcomeOpen(false)}>Cancel</Button>
            <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={handleLogOutcome} data-testid="save-outcome-btn">Log Outcome</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PKMPage;
